// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'admin/core/store';
import {
    Item,
    Translate,
} from '@plesk/ui-library';
import { DashboardItemContainer } from 'admin/dashboard/containers/items/Styles';
import CardHeader from 'admin/dashboard/components/CardHeader';
import StatusesList from 'common/components/StatusesList/StatusesList';
import { ComputeResourceVmStatus } from 'common/api/resources/ComputeResourceVm';
import { StatusType } from 'common/components/Status/Status';

const SERVERS_PATH = '/admin/servers';
// Encoded because `delete failed` contains a space; a no-op for the single-word statuses.
const linkToServersWithStatus = (status: ComputeResourceVmStatus) =>
    `${SERVERS_PATH}?status=${encodeURIComponent(status)}`;

export type ServerItemProps =
    ReturnType<typeof mapStateToProps>;

export const ServerItem: React.FC<ServerItemProps> = ({
    stats,
}) => (
    <DashboardItemContainer>
        <Item
            view="card"
            title={
                <CardHeader to={SERVERS_PATH}>
                    <Translate content="dashboard.cards.servers" params={{ total: stats.total }} />
                </CardHeader>
            }
        >
            <StatusesList
                items={[
                    {
                        status: StatusType.SUCCESS,
                        labelKey: 'computeResource.servers.statuses.started',
                        value: stats.status.started,
                        to: linkToServersWithStatus(ComputeResourceVmStatus.STARTED),
                    },
                    {
                        status: StatusType.STOPPED,
                        labelKey: 'computeResource.servers.statuses.stopped',
                        value: stats.status.stopped,
                        to: linkToServersWithStatus(ComputeResourceVmStatus.STOPPED),
                    },
                    {
                        status: StatusType.PAUSED,
                        labelKey: 'computeResource.servers.statuses.paused',
                        value: stats.status.paused,
                        to: linkToServersWithStatus(ComputeResourceVmStatus.PAUSED),
                    },
                    {
                        status: StatusType.UNAVAILABLE,
                        labelKey: 'computeResource.servers.statuses.unavailable',
                        value: stats.status.unavailable,
                        to: linkToServersWithStatus(ComputeResourceVmStatus.UNAVAILABLE),
                    },
                    {
                        status: StatusType.FAILED,
                        labelKey: 'computeResource.servers.statuses.deleteFailed',
                        value: stats.status['delete failed'],
                        to: linkToServersWithStatus(ComputeResourceVmStatus.DELETE_FAILED),
                    },
                ]}
            />
        </Item>
    </DashboardItemContainer>
);

const mapStateToProps = (state: RootState) => ({
    stats: state.dashboard.data.servers,
});

export default connect(mapStateToProps)(ServerItem);
